/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Global fetchCartData function
// Merged function to fetch cart data and get total frozen weight
/**
 * Fetches cart data and retrieves the total frozen weight from the hidden input.
 * @returns {Promise<{cartTotalWeight: string, cartTotalPrice: number}>} An object containing the cart total weight and cart total price.
 */
// Define the fetchCartData function to fetch and return cart data
async function fetchCartData() {
  try {
    // Fetch cart data from the Shopify cart API
    const response = await fetch("/cart.js");
    const cartData = await response.json();

    // Retrieve the total frozen weight from a hidden input, if available
    const totalFrozenWeightElement =
      document.getElementById("totalFrozenWeight");
    const totalFrozenWeight = totalFrozenWeightElement
      ? totalFrozenWeightElement.value
      : "0";

    // Return an object containing both the total frozen weight and the total price from the cart data
    return {
      cartTotalWeight: totalFrozenWeight,
      cartTotalPrice: cartData.total_price,
      totalFrozenWeight: totalFrozenWeight
    };
  } catch (error) {
    console.error("Error fetching cart data:", error);
    return null;
  }
}

// Weight shipping bar
class WeightShippingBar extends HTMLElement {
  // connectedCallback() {
  //   document.documentElement.addEventListener(
  //     "cart:updated",
  //     this._onCartUpdated.bind(this)
  //   );
  // }

  get threshold() {
    return parseFloat(this.getAttribute("threshold"));
  }

  // _onCartUpdated(event) {
  //   const cart = event.detail.cart;
  //   const totalWeight = cart.cartTotalWeight;
  //   console.log("Total weight from cart:", totalWeight);
  //   console.log("Threshold for shipping bar:", this.threshold);
  //   console.log("Progress bar", Math.min(totalWeight / this.threshold, 1));
  //   this.style.setProperty(
  //     "--progress",
  //     Math.min(totalWeight / this.threshold, 1)
  //   );
  // }
}

window.customElements.define("weight-shipping-bar", WeightShippingBar);

window.standardiseElementHeightBySelector = function (selector) {
  // Find any elements matching the provided selector
  var elements = document.querySelectorAll(selector);
  
  for (i=0; i<elements.length; i++) {
      elements[i].style.height = 'auto';
  }

  // Iterate over all found elements, finding the maximum height
  var maxHeight = 0;
  for (var i=0; i<elements.length; i++) {
    // Get the height of the current element
    var elementHeight = elements[i].offsetHeight;
    
    // If this elements height is higher than our current max, change the max height
    if (elementHeight > maxHeight) {
      maxHeight = elementHeight;
    }
  }
  
  // Iterate over each element and set the height as the discovered maxHeight
  for (i=0; i<elements.length; i++) {
    elements[i].style.height = maxHeight + 'px';
  }
}

window.addEventListener('load', function () {
  standardiseElementHeightBySelector('.product-content__featured-products-list .product-item-meta .product-item-meta__title');
  standardiseElementHeightBySelector('.recently-viewed-products .product-item-meta .product-item-meta__title');
  standardiseElementHeightBySelector('.featured-collections .product-item-meta .product-item-meta__title');
});

window.addEventListener('resize', function() {
  standardiseElementHeightBySelector('.product-content__featured-products-list .product-item-meta .product-item-meta__title');
  standardiseElementHeightBySelector('.recently-viewed-products .product-item-meta .product-item-meta__title');
  standardiseElementHeightBySelector('.featured-collections .product-item-meta .product-item-meta__title');
});
// Loyality Lion Init
window.addEventListener("load", (event) => {
  loyaltylion.init({
    token: 'c9f6c5f25f040ece5744e37e8b8c7f67',
  })
});
// Loyality Lion Refresh On Variant Change
document.addEventListener('variant:changed', function(event) {
  loyaltylion.ui.refresh()
});

//Show and Hide Loyality Points on focus
document.addEventListener("DOMContentLoaded", (event) => {
  const loyaltyIcon = document.querySelector(".loyality-lion-image-icon");
  const loyalityPopup = document.querySelector(".loyality-lion-popup-container");
  function showPopup() {
      loyalityPopup.classList.add("active");
  }
  function hidePopup() {
      loyalityPopup.classList.remove("active");
  }
if(loyaltyIcon){
      loyaltyIcon.addEventListener("focus", showPopup);
      loyaltyIcon.addEventListener("focusout", hidePopup);
  }
});

//Remove position inline style on klaviyo notify button to align the elements on the product card
document.addEventListener("DOMContentLoaded", (event) => {
  const klaviyoBIS = document.querySelectorAll(".klaviyo-button-container");
  for(i = 0; i < klaviyoBIS.length; i++){
    klaviyoBIS[i].style.removeProperty('position');
  }
});

//Click event so when review stars is click, smooth scroll to element and simulate click on review tab to open it.
document.addEventListener("DOMContentLoaded", (event) => {
  const feefoStarContainer = document.querySelector(".feefo-review-badge-wrapper-product");
  const productContent = document.querySelector(".product-content");
  const reviewsTab = document.querySelector(".tabs-nav__item.reviews");
  if(feefoStarContainer) {
    feefoStarContainer.addEventListener("click", scrollToReview)
    function scrollToReview() {
    productContent.scrollIntoView({ behavior: "smooth", block: "start" });
    reviewsTab.click();
    }
    //Mutation Observer so if feefo product has no stars, hide the reviews tab.
    const domObserver = new MutationObserver(() => {
    const feefoReviewStars = document.querySelector('.feefo-review-badge-wrapper-product .summary-rating');
    if (feefoReviewStars) {
        reviewsTab.style.display = "block"
    }
    else {
        reviewsTab.style.display = "none"
    }
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
  }
});

//Added function so when Recharge Widget is loaded, add 'i' icon and move product picked tally after 1 second of it loading
window.addEventListener('ReChargeWidget-loaded', (event) => {
  function addInfoIcon() {
    const rechargeProducts = document.querySelectorAll('.rb-img');
    for (var i = 0; i < rechargeProducts.length; i++) {
      const infoIcon = document.createElement('img');
      infoIcon.src = "https://cdn.shopify.com/s/files/1/0806/3719/8642/files/i_1.svg?v=1742222357";
      infoIcon.className = "recharge_info-icon"
      rechargeProducts[i].append(infoIcon);
    }
  }
  setTimeout(function() { 
    addInfoIcon(); 
    const rechargeaMainContent = document.querySelector(".rb-container form div");
    const rechargeStickyContent = document.querySelector(".rb-sticky-content");
    if(rechargeaMainContent) {
      rechargeaMainContent.before(rechargeStickyContent);
    }
  }, 1000);
});

// Update the variant price, weight and availability in collection pages
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('.product-form__buy-buttons').forEach(function(formWrapper) {
    var selectWrapper = formWrapper.querySelector('.collection-select-wrapper');
    if (!selectWrapper) return;
    var select = selectWrapper.querySelector('.product-item__variant-select');
    var priceBox = formWrapper.querySelector('.collection-variant-price');
    var addToCartBtn = formWrapper.querySelector('button[type="submit"], .product-form__add-button');
    var prices = {};
    try {
      prices = JSON.parse(selectWrapper.getAttribute('data-variant-prices').replace(/&quot;/g,'"'));
    } catch(e) {}

    if (select && priceBox) {
      select.addEventListener('change', function() {
        var variantId = select.value;
        var variant = prices[variantId];
        if (variant) {
          var variantName = ' for ' + select.options[select.selectedIndex].text;
          var html = '<span class="price">' + variant.price + '</span>';
          if (variant.compare_at_price && variant.compare_at_price !== variant.price) {
            html += '<span class="price price--compare">' + variant.compare_at_price + '</span>';
          }
          html += '<span class="variant-title">' + variantName + '</span>';
          priceBox.innerHTML = html;

          // Enable or disable Add to Cart button based on variant availability
          if (addToCartBtn) {
            if (select.options[select.selectedIndex].disabled) {
              addToCartBtn.setAttribute('disabled', 'disabled');
            } else {
              addToCartBtn.removeAttribute('disabled');
            }
          }
        }
      });
    }
  });
});

// Shows/hides subscription widget based on delivery location
document.addEventListener("bundling-widget:ready", () => {
  const allowSubscriptions =
    window.rechargeSettings?.allowSubscriptions ?? true;
    
  if (!allowSubscriptions) {
    const buildABoxContainer = document.querySelector("#build-a-box");
    const observer = new MutationObserver(() => {
      const input = buildABoxContainer.querySelector("input[type='checkbox']");
      if (input) {
        observer.disconnect();
        input.checked = false;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        const subContainer = input.closest(".rb-subscribe-and-save-container");
        if (subContainer) subContainer.style.display = "none";
        else input.style.display = "none";
      }
    });

    observer.observe(buildABoxContainer, { childList: true, subtree: true });
  }
});